import { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { toast } from "react-toastify";
import { z } from "zod";
import { makeSxStyles, ConfirmDialog } from "ui";
import { useAdminOrganizationContextControllerGetCurrent } from "service";
import { optionalEmailSchema } from "@/shared/utils/email-validation";
import useUpdateReplyEmail from "../hooks/useUpdateReplyEmail";

const validationSchema = z.object({
  replyToEmail: optionalEmailSchema.optional(),
});

// Tab 1 of the Settings screen (ESR-1616) - lets an org admin view/edit
// their organization's customer reply email at any time, using the same
// self-service endpoint as the post-signup setup step (ESR-1614).
const ReplyEmailTab = () => {
  const styles = useStyles();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const { data, isLoading: isLoadingCurrent } =
    useAdminOrganizationContextControllerGetCurrent({});

  const { updateReplyEmail, isLoading: isSaving } = useUpdateReplyEmail(() => {
    toast.success("Email Successfully Updated.");
  });

  const {
    values,
    handleChange,
    handleBlur,
    errors,
    touched,
    dirty,
    isValid,
    submitForm,
  } = useFormik({
    initialValues: {
      replyToEmail: (data?.data?.replyToEmail as string | null) ?? "",
    },
    enableReinitialize: true,
    validationSchema: toFormikValidationSchema(validationSchema),
    onSubmit: () => setIsConfirmOpen(true),
  });

  const isClearingEmail = !values.replyToEmail.trim();

  if (isLoadingCurrent) {
    return (
      <Box sx={styles.loading}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      component="form"
      sx={styles.container}
      onSubmit={(e) => {
        e.preventDefault();
        submitForm();
      }}
    >
      <Box sx={styles.field}>
        <Typography variant="labelLarge" sx={styles.label}>
          Customer reply email
        </Typography>
        <Typography variant="bodySmall" sx={styles.description}>
          Add an email to receive customer replies if they reply to email
          notifications sent via SnowSync platform.
          <br />
          If you leave it empty, replies will be sent to SnowSync.
        </Typography>
        <TextField
          id="replyToEmail"
          placeholder="name@organization.com"
          value={values.replyToEmail}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.replyToEmail && !!errors.replyToEmail}
          helperText={touched.replyToEmail && errors.replyToEmail}
          sx={styles.input}
        />
      </Box>
      <Button
        type="submit"
        variant="contained"
        disabled={!dirty || !isValid || isSaving}
        data-testid="save-reply-email-button"
      >
        Save Changes
      </Button>
      <ConfirmDialog
        open={isConfirmOpen}
        title={isClearingEmail ? "Remove Email?" : "Update Email?"}
        description={
          isClearingEmail
            ? "Are you sure you want to remove the customer reply email? All customer emails will be sent to default SnowSync email."
            : "Are you sure you want to update customer reply email? All customer emails will be sent to the new email address."
        }
        cancelText="Cancel"
        confirmText={isClearingEmail ? "Remove" : "Update"}
        sx={styles.confirmDialog}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={(onClose) => {
          onClose();
          updateReplyEmail(values.replyToEmail);
        }}
      />
    </Box>
  );
};

const useStyles = makeSxStyles(() => ({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "24px",
    maxWidth: "740px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  label: {
    color: "grey.100",
  },
  description: {
    color: "grey.200",
    mt: "4px",
  },
  input: {
    mt: "12px",
    width: "360px",
    maxWidth: "100%",
  },
  confirmDialog: {
    "& .MuiPaper-root": {
      maxWidth: "400px",
    },
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    mt: "40px",
  },
}));

export default ReplyEmailTab;
